/**
 * Phase 37 — Frequency preset → RFC-5545 RRULE string serialiser.
 *
 * 5 hardcoded presets + Custom. Output is validated server-side via rrule.js
 * inside era-api `rruleExpander.ts` (Phase 35-01) — this serialiser is the
 * sole FE writer of RRULE strings; we never parse/expand recurrence on FE.
 *
 * D-13 LOCKED outputs:
 *   - "once"   → null                                        (no recurrence)
 *   - "daily"  → "RRULE:FREQ=DAILY"
 *   - "bid"    → "RRULE:FREQ=DAILY;BYHOUR=8,20"               (RESEARCH Pitfall 11)
 *   - "tid"    → "RRULE:FREQ=DAILY;BYHOUR=8,14,20"
 *   - "eod"    → "RRULE:FREQ=DAILY;INTERVAL=2"
 *   - "custom" → "RRULE:FREQ=WEEKLY[;BYDAY=...][;COUNT=N | ;UNTIL=YYYYMMDDTHHMMSS]"
 *
 * IMPORTANT: When user picks "bid" / "tid", the FE-stored `dateTime` hour MUST
 * be set to the FIRST BYHOUR value (8) so the synthetic id ISO matches the
 * expander's first occurrence (RESEARCH Pitfall 11). FrequencyPicker handles
 * this rewrite — serializeRRule itself just produces the string.
 */

export type FrequencyPreset =
	| "once"
	| "daily"
	| "bid"
	| "tid"
	| "eod"
	| "custom";

export type Weekday = "MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU";

export interface CustomFrequencyState {
	weekdays: Weekday[];
	endMode: "until" | "count";
	until?: string; // YYYY-MM-DD
	count?: number;
}

export function serializeRRule(
	preset: FrequencyPreset,
	custom?: CustomFrequencyState,
): string | null {
	switch (preset) {
		case "once":
			return null;
		case "daily":
			return "RRULE:FREQ=DAILY";
		case "bid":
			return "RRULE:FREQ=DAILY;BYHOUR=8,20";
		case "tid":
			return "RRULE:FREQ=DAILY;BYHOUR=8,14,20";
		case "eod":
			return "RRULE:FREQ=DAILY;INTERVAL=2";
		case "custom": {
			if (!custom) return null;
			const days =
				custom.weekdays.length > 0 ? `;BYDAY=${custom.weekdays.join(",")}` : "";
			let end = "";
			if (
				custom.endMode === "count" &&
				typeof custom.count === "number" &&
				custom.count > 0
			) {
				end = `;COUNT=${custom.count}`;
			} else if (custom.endMode === "until" && custom.until) {
				// YYYY-MM-DD → YYYYMMDDT235959 (RFC-5545 DATE-TIME, end-of-day, server interprets in event TZ)
				end = `;UNTIL=${custom.until.replace(/-/g, "")}T235959`;
			}
			return `RRULE:FREQ=WEEKLY${days}${end}`;
		}
	}
}
